<template>
  <!-- Add Schedule Modal -->
  <div id="schedule_timing" class="modal fade dq-schedule-modal" role="dialog" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-lg dq-schedule-dialog" role="document">
      <div class="modal-content dq-modal-content">
        <form @submit.prevent="onSubmit">
          <!-- Header -->
          <div class="modal-header dq-modal-header">
            <div class="dq-titlewrap">
              <div class="dq-hero dq-hero-indigo">
                <i class="ti ti-calendar-event"></i>
              </div>
              <div>
                <h5 class="modal-title dq-title mb-0">{{ editingId ? 'Edit Schedule' : 'Add Schedule' }}</h5>
                <div class="dq-subtitle">
                  Assign a shift and define time windows & rules for attendance.
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-close dq-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i class="ti ti-x"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body dq-body">
            <!-- Top: People -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-indigo">
                    <i class="ti ti-users"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Assignment</div>
                    <div class="dq-help">
                      Choose the department and employee for this schedule.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-sm-6">
                    <label class="form-label dq-label">
                      Department <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-select dq-input"
                      v-model.number="form.department_id"
                      required
                    >
                      <option :value="null" disabled>Select</option>
                      <option v-for="d in departments" :key="d.id" :value="d.id">
                        {{ d.name }}
                      </option>
                    </select>
                    <div class="dq-hint">Employees can be filtered by department.</div>
                  </div>

                  <div class="col-sm-6">
                    <label class="form-label dq-label">
                      Employee <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-select dq-input"
                      v-model.number="form.employee_id"
                      required
                    >
                      <option :value="null" disabled>Select</option>
                      <option v-for="e in employees" :key="e.id" :value="e.id">
                        {{ employeeLabel(e) }}
                      </option>
                    </select>
                    <div class="dq-hint">
                      {{ form.department_id ? "Showing department employees." : "Showing all employees." }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Shift + Date -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-briefcase"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Shift & Date</div>
                    <div class="dq-help">
                      Pick date and the shift profile for this assignment.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-sm-6">
                    <label class="form-label dq-label">Date <span class="text-danger">*</span></label>
                    <input
                      class="form-control dq-input"
                      type="date"
                      v-model="form.date"
                      required
                    />
                  </div>

                  <div class="col-sm-6">
                    <label class="form-label dq-label">Select Shift</label>
                    <vue-select
                      v-model="form.shift_name"
                      :options="['Day Shift', 'Night Shift']"
                      placeholder="Select Shift"
                      class="dq-vselect"
                    />
                    <div class="dq-hint">Optional — you can still set times below.</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Time Windows -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-green">
                    <i class="ti ti-clock"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Time Windows</div>
                    <div class="dq-help">
                      Define start/end windows (min/ideal/max) for flexibility.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <!-- Start Window -->
                <div class="dq-grid-head">
                  <div class="dq-grid-title">
                    <i class="ti ti-login me-1"></i> Start Window
                  </div>
                  <div class="dq-grid-sub">Minimum, required start and maximum.</div>
                </div>

                <div class="row g-3">
                  <div class="col-sm-4">
                    <label class="form-label dq-label">Min Start Time</label>
                    <input
                      class="form-control dq-input"
                      type="time"
                      v-model="form.min_start_time"
                    />
                  </div>

                  <div class="col-sm-4">
                    <label class="form-label dq-label">
                      Start Time <span class="text-danger">*</span>
                    </label>
                    <input
                      class="form-control dq-input dq-focus"
                      type="time"
                      v-model="form.start_time"
                      required
                    />
                  </div>

                  <div class="col-sm-4">
                    <label class="form-label dq-label">Max Start Time</label>
                    <input
                      class="form-control dq-input"
                      type="time"
                      v-model="form.max_start_time"
                    />
                  </div>
                </div>

                <hr class="dq-divider" />

                <!-- End Window -->
                <div class="dq-grid-head">
                  <div class="dq-grid-title">
                    <i class="ti ti-logout me-1"></i> End Window
                  </div>
                  <div class="dq-grid-sub">Minimum, required end and maximum.</div>
                </div>

                <div class="row g-3">
                  <div class="col-sm-4">
                    <label class="form-label dq-label">Min End Time</label>
                    <input
                      class="form-control dq-input"
                      type="time"
                      v-model="form.min_end_time"
                    />
                  </div>

                  <div class="col-sm-4">
                    <label class="form-label dq-label">
                      End Time <span class="text-danger">*</span>
                    </label>
                    <input
                      class="form-control dq-input dq-focus"
                      type="time"
                      v-model="form.end_time"
                      required
                    />
                  </div>

                  <div class="col-sm-4">
                    <label class="form-label dq-label">Max End Time</label>
                    <input
                      class="form-control dq-input"
                      type="time"
                      v-model="form.max_end_time"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Rules -->
            <div class="dq-section mt-3">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-amber">
                    <i class="ti ti-settings"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Rules</div>
                    <div class="dq-help">
                      Break time and publication controls for this schedule.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3 align-items-end">
                  <div class="col-sm-4">
                    <label class="form-label dq-label">Break Minutes</label>
                    <input
                      class="form-control dq-input"
                      type="number"
                      min="0"
                      max="720"
                      v-model.number="form.break_minutes"
                    />
                    <div class="dq-hint">0–720 minutes.</div>
                  </div>

                  <div class="col-sm-4">
                    <div class="dq-switch-card">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Accept Extra Hours</div>
                        <div class="dq-switch-sub">Allow overtime clocking.</div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          type="checkbox"
                          class="form-check-input dq-switch"
                          id="switch1"
                          v-model="form.accept_extra_hours"
                        />
                        <label class="form-check-label" for="switch1"></label>
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4">
                    <div class="dq-switch-card">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Publish</div>
                        <div class="dq-switch-sub">Make visible to employee.</div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          type="checkbox"
                          class="form-check-input dq-switch"
                          id="switch2"
                          v-model="form.published"
                        />
                        <label class="form-check-label" for="switch2"></label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Small preview card -->
            <div class="dq-preview mt-3">
              <div class="dq-preview-left">
                <div class="dq-preview-title">
                  <i class="ti ti-eye me-1"></i> Quick Preview
                </div>
                <div class="dq-preview-sub">
                  {{ form.date || "—" }} •
                  {{ form.start_time || "—" }} → {{ form.end_time || "—" }} •
                  Break {{ form.break_minutes ?? 0 }} mins
                </div>
              </div>
              <span
                class="dq-pill"
                :class="form.published ? 'dq-pill-live' : 'dq-pill-draft'"
              >
                <i class="ti" :class="form.published ? 'ti-broadcast' : 'ti-file'"></i>
                {{ form.published ? "Published" : "Draft" }}
              </span>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-footer">
            <button class="btn btn-white dq-btn dq-btn-muted" type="button" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-primary dq-btn dq-btn-primary submit-btn" type="submit">
              <span v-if="submitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingId ? 'Save Changes' : 'Submit' }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useScheduleStore } from "@/stores/scheduleStore";
import { useDepartmentStore } from "@/stores/departmentStore";
import { useEmployeeStore } from "@/stores/employeeStore";

const props = defineProps({
  editItem: { type: Object, default: null },
});

const emit = defineEmits(["saved"]);

const scheduleStore = useScheduleStore();
const departStore = useDepartmentStore();
const empStore = useEmployeeStore();

const submitting = computed(() => scheduleStore.loading);
const editingId = ref(null);

const emptyForm = () => ({
  department_id: null,
  employee_id: null,
  date: "",
  shift_name: "",
  min_start_time: "",
  start_time: "",
  max_start_time: "",
  min_end_time: "",
  end_time: "",
  max_end_time: "",
  break_minutes: 60,
  accept_extra_hours: true,
  published: true,
});

const form = ref(emptyForm());

// Load data once
onMounted(async () => {
  await Promise.all([departStore.fetchDepartments(), empStore.fetchEmployees()]);
});

// Lists from stores
const departments = computed(() => departStore.departments || []);

// Optional: filter employees by selected department
const allEmployees = computed(() => empStore.employees || []);
const employees = computed(() => {
  const deptId = form.value.department_id;
  if (!deptId) return allEmployees.value;
  return allEmployees.value.filter((e) => Number(e.department_id) === Number(deptId));
});

watch(
  () => props.editItem,
  (row) => {
    if (!row?.id) {
      editingId.value = null;
      form.value = emptyForm();
      return;
    }

    editingId.value = row.id;
    const employee = (empStore.employees || []).find((e) => e.id === row.employee?.id);
    form.value = {
      department_id: employee?.department_id ?? null,
      employee_id: row.employee?.id ?? null,
      date: row.date || "",
      shift_name: row.shift_name || "",
      min_start_time: row.min_start_time || "",
      start_time: row.start_time || "",
      max_start_time: row.max_start_time || "",
      min_end_time: row.min_end_time || "",
      end_time: row.end_time || "",
      max_end_time: row.max_end_time || "",
      break_minutes: row.break_minutes ?? 60,
      accept_extra_hours: row.accept_extra_hours ?? true,
      published: row.published ?? false,
    };
  },
  { immediate: true }
);

// Reset employee when department changes
watch(
  () => form.value.department_id,
  () => {
    if (!editingId.value) {
      form.value.employee_id = null;
    }
  }
);

function employeeLabel(e) {
  return (
    e.name ||
    [e.first_name, e.last_name].filter(Boolean).join(" ") ||
    e.email ||
    `#${e.id}`
  );
}

async function onSubmit() {
  if (!form.value.department_id || !form.value.employee_id) return;

  const payload = { ...form.value };
  if (editingId.value) {
    await scheduleStore.update(editingId.value, payload);
  } else {
    await scheduleStore.create(payload);
  }
  emit("saved");

  editingId.value = null;
  form.value = emptyForm();

  const el = document.getElementById("schedule_timing");
  if (el && window.bootstrap) {
    const modal =
      window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
    modal.hide();
  }
}
</script>

<style scoped>
/* Wider, premium modal */
.dq-schedule-dialog {
  max-width: 980px; /* increased width */
}
@media (max-width: 768px) {
  .dq-schedule-dialog {
    max-width: 96%;
  }
}

.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 80px rgba(15, 23, 42, 0.12);
}

/* Header */
.dq-modal-header {
  padding: 18px 22px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.12), #fff);
}
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 900;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero */
.dq-hero {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-indigo {
  background: rgba(99, 102, 241, 0.16);
  color: rgb(79, 70, 229);
}

/* Body */
.dq-body {
  padding: 18px 22px 10px;
}

/* Sections */
.dq-section {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.02);
  overflow: hidden;
}
.dq-section-head {
  padding: 14px 14px 0 14px;
}
.dq-section-body {
  padding: 14px;
}
.dq-section-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.dq-section-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex: 0 0 auto;
}
.dq-h6 {
  font-weight: 900;
  letter-spacing: -0.2px;
}
.dq-help {
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
  line-height: 1.35;
}
.dq-label {
  font-size: 0.82rem;
  font-weight: 800;
}
.dq-hint {
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

/* Badges */
.dq-badge-indigo {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}
.dq-badge-blue {
  background: rgba(59, 130, 246, 0.12);
  color: rgb(37, 99, 235);
}
.dq-badge-green {
  background: rgba(34, 197, 94, 0.12);
  color: rgb(22, 163, 74);
}
.dq-badge-amber {
  background: rgba(245, 158, 11, 0.16);
  color: rgb(180, 83, 9);
}

/* Inputs */
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
  transition: all 0.15s ease;
}
.dq-input:focus {
  border-color: rgba(99, 102, 241, 0.55);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}
.dq-focus {
  border-color: rgba(99, 102, 241, 0.35);
}

/* vue-select polish (works if vue-select passes classes through) */
.dq-vselect :deep(.vs__dropdown-toggle) {
  border-radius: 14px;
  padding: 4px 8px;
}
.dq-vselect :deep(.vs__search),
.dq-vselect :deep(.vs__selected) {
  font-weight: 700;
}

/* Window headings */
.dq-grid-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.dq-grid-title {
  font-weight: 900;
  color: rgba(15, 23, 42, 0.9);
}
.dq-grid-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}

.dq-divider {
  border: none;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  margin: 14px 0;
}

/* Switch cards */
.dq-switch-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  height: 100%;
}
.dq-switch-title {
  font-weight: 900;
  letter-spacing: -0.15px;
}
.dq-switch-sub {
  font-size: 0.78rem;
  color: rgba(15, 23, 42, 0.55);
}
.dq-switch {
  width: 46px;
  height: 24px;
}

/* Preview */
.dq-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px;
  border-radius: 18px;
  border: 1px dashed rgba(15, 23, 42, 0.14);
  background: rgba(99, 102, 241, 0.05);
}
.dq-preview-title {
  font-weight: 900;
}
.dq-preview-sub {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Pills */
.dq-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 0.78rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
}
.dq-pill-live {
  background: rgba(34, 197, 94, 0.10);
  color: rgb(22, 163, 74);
  border-color: rgba(34, 197, 94, 0.18);
}
.dq-pill-draft {
  background: rgba(15, 23, 42, 0.05);
  color: rgba(15, 23, 42, 0.75);
}

/* Footer buttons */
.dq-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
}
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 800;
}
.dq-btn-primary {
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}
</style>
